// Volatility Estimator — PSD §3.4 (F3.4). Builder Step 5.

import { useState, useMemo } from 'react';
import type { Comparable } from '@backsolve/shared-logic/volatility';
import { estimateVolatility } from '@backsolve/shared-logic/volatility';
import { ScrubInput, ExpressionInput, NumericDisplay, RangeSlider } from 'calc-controls';
import { Tooltip } from '../common/Tooltip';

interface VolatilityEstimatorProps {
  volatility: number;
  onChange: (volatility: number) => void;
}

export function VolatilityEstimator({ volatility, onChange }: VolatilityEstimatorProps) {
  const [comparables, setComparables] = useState<Comparable[]>([]);
  const [name, setName] = useState('');
  const [returns, setReturns] = useState('');
  const [debtToEquity, setDebtToEquity] = useState('');

  const estimate = useMemo(() => {
    if (comparables.length < 3) return null;
    return estimateVolatility(
      comparables,
      debtToEquity ? { debtToEquity: parseFloat(debtToEquity), unleveredBeta: 1.0 } : undefined,
    );
  }, [comparables, debtToEquity]);

  const addComparable = () => {
    if (!name || !returns) return;
    const rets = returns
      .split(/[,\s]+/)
      .map((r) => parseFloat(r))
      .filter((r) => !isNaN(r));
    if (rets.length === 0) return;
    setComparables([...comparables, { name, returns: rets }]);
    setName('');
    setReturns('');
  };

  return (
    <div className="volatility-estimator">
      <h4>Peer-Set Volatility Estimation</h4>
      <p className="field-hint">Add ≥ 3 public comparables with monthly returns to estimate volatility.</p>

      <div className="form-row">
        <input
          type="text"
          placeholder="Company name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Comparable company name"
        />
        <input
          type="text"
          placeholder="Monthly returns (comma-separated)"
          value={returns}
          onChange={(e) => setReturns(e.target.value)}
          aria-label="Monthly returns"
          style={{ flex: 2 }}
        />
        <button className="btn btn-secondary" onClick={addComparable}>
          Add
        </button>
      </div>

      {comparables.length > 0 && (
        <table className="data-table mt-3">
          <thead>
            <tr>
              <th>Company</th>
              <th>Periods</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {comparables.map((c, i) => (
              <tr key={i}>
                <td>{c.name}</td>
                <td>{c.returns.length}</td>
                <td>
                  <button
                    className="btn btn-icon"
                    onClick={() => setComparables(comparables.filter((_, j) => j !== i))}
                    aria-label={`Remove ${c.name}`}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="form-group mt-3">
        <ExpressionInput
          label={
            <>
              Debt-to-Equity
              <Tooltip
                label="Optional leverage ratio used to deleverage peer betas (tax rate fixed at 21%)."
                ariaLabel="Help: Debt-to-Equity"
              />
            </>
          }
          value={debtToEquity ? parseFloat(debtToEquity) : 0}
          onChange={(v) => setDebtToEquity(v > 0 ? String(v) : '')}
          min={0}
          decimals={2}
          id="d-e"
        />
      </div>

      {estimate && (
        <div className="estimate-results">
          <h5>Estimated Volatility</h5>
          <p className="result-caption">
            Per-comparable volatilities are annualized from monthly returns
            (&radic;12). Mean is the simple peer average; Unlevered / Relevered
            apply the optional D/E adjustment with a 21% tax rate.
          </p>
          <div className="headline-grid">
            <div className="metric">
              <span className="metric-label">Mean (Asset)</span>
              <NumericDisplay
                inline
                value={estimate.mean * 100}
                unit="%"
                decimals={1}
              />
            </div>
            <div className="metric">
              <span className="metric-label">Equity Vol</span>
              <NumericDisplay
                inline
                value={estimate.equityVolatility * 100}
                unit="%"
                decimals={1}
              />
            </div>
            <div className="metric">
              <span className="metric-label">Unlevered</span>
              <NumericDisplay
                inline
                value={estimate.unleveredVolatility * 100}
                unit="%"
                decimals={1}
              />
            </div>
            <div className="metric">
              <span className="metric-label">Relevered</span>
              <NumericDisplay
                inline
                value={estimate.releveredVolatility * 100}
                unit="%"
                decimals={1}
              />
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => onChange(estimate.mean)}>
            Use {((estimate.mean ?? 0) * 100).toFixed(1)}%
          </button>
        </div>
      )}

      <div className="form-group mt-3">
        <RangeSlider
          label="Volatility (quick set)"
          tooltip="Drag to set annualized equity volatility (σ) quickly. Use the precise entry field below for expression input."
          value={volatility}
          onChange={onChange}
          min={0.05}
          max={2}
          step={0.01}
          decimals={2}
        />
      </div>
      <div className="form-group">
        <ScrubInput
          label="Volatility (manual override)"
          tooltip="Annualized equity volatility (σ) fed to Black-Scholes. The manual value always wins; click a Use button above to copy an estimate here. Drag ↕ or double-click to enter an expression."
          value={volatility}
          onChange={onChange}
          min={0.05}
          max={2}
          step={0.01}
          decimals={2}
        />
      </div>
    </div>
  );
}
